// The block handle (⠿) left of the hovered block: drag it to move the block,
// click it for the block menu (block-menu.tsx).
//
// Built on `@tiptap/extension-drag-handle`. Its `NodeRange` extension is not
// registered (only the package is a dependency): it would change Mod-A and
// Shift-↑↓ into range selections.
//
// Promise:
// - Moving a block keeps its block id. The extension starts a drag with
//   `view.dragging = { slice, move: true }` and leaves the drop to
//   prosemirror-view, which calls `transformPasted` before clearing
//   `view.dragging`; the block id plugin keeps ids while `dragging.move` is set.
// - A drag from the handle is always a move (`dragCopies` false). The extension
//   hard-codes move, but prosemirror-view decides whether to delete the source
//   from the modifier (Alt on mac, Ctrl elsewhere): with it held, the id was kept
//   and the source stayed too - two blocks with one id, and which one kept it
//   depended on where it was dropped. The price: a text selection can no longer
//   be copy-dragged with the modifier (deciding only for handle drags would mean
//   copying prosemirror-view's own platform test, and a drift there would bring
//   the duplicate ids back).
// - The handle is offered for top-level blocks and for the direct children of a
//   quote (a block dropped into a quote must be able to get out again). For a
//   quote whose first line is a paragraph, that line takes the quote itself;
//   the gap between a quote's lines offers nothing, so the handle stays where
//   it was (as between top-level blocks).
// - Its target is decided by the row, not the pointer: the pointer crosses the
//   left margin on its way to the handle, and there the position resolved to
//   the container, not the line beside the handle. Each mousemove is sent again
//   at the middle of the body's width.
// - It sits at the middle of the block's first line (blocks differ in where the
//   first line starts: up to 24px), and at the body's left edge for blocks
//   inside a quote (the column of handles stays straight).
// - Typing hides it (it stays wherever the mouse rests otherwise); leaving it
//   for anywhere but the body hides it; dropping shows it again where the mouse is.
// - The drag image is a faint copy of the block, and the cursor says "move".
import type { Editor } from "@tiptap/core";
import { DragHandle } from "@tiptap/extension-drag-handle";
import { BLOCK_ID_ATTR } from "@trameria/editor";

export const DRAG_HANDLE_CLASS = "trameria-drag-handle";

/** Dispatched on the editor's DOM when the handle is clicked (block-menu.tsx draws the menu). */
export const BLOCK_MENU_EVENT = "trameria:block-menu";

export interface BlockMenuEventDetail {
  /** The block's id; null for a block inside a quote (ids are top-level only). */
  blockId: string | null;
  /** Where the block is (the only handle on a block without an id). */
  pos: number;
  /** The handle's rectangle (the menu is placed at it). */
  anchor: DOMRect;
  /** Must be called when the menu closes: unlocks and hides the handle. */
  close(): void;
}

/** prosemirror-view's `dragCopies`: never a copy (see the top of this file). */
export function trameriaDragCopies(): boolean {
  return false;
}

/** The handle's height in document.css; used when it cannot be measured. */
export const DEFAULT_HANDLE_HEIGHT = 28;

export const TrameriaDragHandle = DragHandle.configure({
  nested: {
    // `allowedContainers` cannot say "top level or inside a quote": it requires
    // every candidate deeper than 1 to be inside one of them, which removed
    // ordinary paragraphs. The default rules would drop list wrappers, and here
    // a list of one item is the block itself.
    defaultRules: false,
    // The default "left" prefers the parent near the left edge; the handle is
    // outside that edge, so the target changed on the way to it.
    edgeDetection: "none",
    rules: [
      {
        id: "trameriaTopLevelOrQuoteChild",
        evaluate: ({ depth, parent }) =>
          depth <= 1 || parent?.type.name === "blockquote" ? 0 : 1000,
      },
      {
        id: "trameriaQuoteFirstParagraph",
        evaluate: ({ node, parent, isFirst }) =>
          parent?.type.name === "blockquote" &&
          isFirst &&
          node.type.name === "paragraph"
            ? 1000
            : 0,
      },
      {
        // Between a quote's lines the position resolves to the quote itself; it
        // is not offered there (every enclosing quote too, or nested quotes
        // jumped to the outer quote's first line).
        id: "trameriaQuoteGap",
        evaluate: ({ node, $pos }) =>
          node.type.name === "blockquote" && !$pos.parent.isTextblock
            ? 1000
            : 0,
      },
    ],
  },
  onNodeChange(data) {
    const { editor, node } = data;
    // The implementation passes `pos` too; the 3.30.5 types do not say so.
    const pos = (data as { pos?: number }).pos ?? -1;
    const element = editor.view.dom.parentElement?.querySelector<HTMLElement>(
      `.${DRAG_HANDLE_CLASS}`,
    );
    if (!element) return;
    bindLeave(element, editor);
    bindMenu(element, editor);
    bindHideWhileTyping(element, editor);
    bindGhost(element, editor);
    const blockId = node?.attrs?.[BLOCK_ID_ATTR];
    if (typeof blockId === "string" && blockId.length > 0)
      element.dataset.blockId = blockId;
    else element.removeAttribute("data-block-id");
    if (pos >= 0) element.dataset.dragPos = String(pos);
    else element.removeAttribute("data-drag-pos");
    alignHandle(
      element,
      editor,
      node && pos >= 0 ? editor.view.nodeDOM(pos) : null,
    );
  },
  render() {
    const element = document.createElement("div");
    element.className = `drag-handle ${DRAG_HANDLE_CLASS}`;
    // A mouse affordance; the block menu has no keyboard path here either.
    element.setAttribute("aria-hidden", "true");
    element.append("⠿");
    // Inline on purpose: the plugin shows it by clearing the inline value, so a
    // hidden state in a class could never be undone.
    element.style.visibility = "hidden";
    return element;
  },
});

const rowBound = new WeakSet<HTMLElement>();

/** Resolves the handle's target by row (see the top of this file). Call once per editor. */
export function bindRowResolution(editor: Editor): void {
  const dom = editor.view.dom;
  if (rowBound.has(dom)) return;
  rowBound.add(dom);
  dom.addEventListener("mousemove", (e) => {
    const style = getComputedStyle(dom);
    const rect = dom.getBoundingClientRect();
    const left = rect.left + Number.parseFloat(style.paddingLeft || "0");
    const right = rect.right - Number.parseFloat(style.paddingRight || "0");
    const center = Math.round((left + right) / 2);
    const atom = atomBlockUnder(dom, center, e.clientY);
    const atomRect = atom?.getBoundingClientRect();
    const y = atomRect
      ? atomRowY({ blockTop: atomRect.top, blockHeight: atomRect.height })
      : e.clientY;
    if (e.clientX === center && y === e.clientY) return;
    // The plugin handles the last mousemove of a frame, so this one wins.
    dom.dispatchEvent(
      new MouseEvent("mousemove", {
        clientX: center,
        clientY: y,
        bubbles: true,
      }),
    );
  });
}

/** The top-level atom block (contenteditable="false") under a point, or null. */
function atomBlockUnder(
  dom: HTMLElement,
  x: number,
  y: number,
): HTMLElement | null {
  const el = document.elementFromPoint?.(x, y);
  if (!(el instanceof HTMLElement) || el === dom || !dom.contains(el))
    return null;
  let cur: HTMLElement | null = el;
  while (cur.parentElement !== dom) {
    cur = cur.parentElement;
    if (!cur || !dom.contains(cur)) return null;
  }
  return cur.getAttribute("contenteditable") === "false" ? cur : null;
}

/**
 * The y to give the plugin over an atom block: its upper part. The plugin
 * takes an atom only as `$pos.nodeAfter`, and `posAtCoords` over the lower half
 * of a textless node resolves after it - the handle moved to the next block.
 */
export function atomRowY(input: {
  blockTop: number;
  blockHeight: number;
}): number {
  return Math.round(input.blockTop + Math.max(1, input.blockHeight / 4));
}

/** Sets `--trameria-handle-dx` / `-dy` (see the top of this file). */
function alignHandle(element: HTMLElement, editor: Editor, dom: unknown): void {
  if (!(dom instanceof HTMLElement)) {
    element.style.removeProperty("--trameria-handle-dy");
    element.style.removeProperty("--trameria-handle-dx");
    return;
  }
  const block = dom.getBoundingClientRect();
  const line = firstLineRect(dom);
  const view = editor.view.dom;
  const contentLeft =
    view.getBoundingClientRect().left +
    Number.parseFloat(getComputedStyle(view).paddingLeft || "0");
  element.style.setProperty(
    "--trameria-handle-dx",
    `${Math.round(contentLeft - block.left)}px`,
  );
  element.style.setProperty(
    "--trameria-handle-dy",
    `${handleOffsetY({
      blockTop: block.top,
      blockHeight: block.height,
      line: line && { top: line.top, height: line.height },
      handleHeight: element.offsetHeight || DEFAULT_HANDLE_HEIGHT,
    })}px`,
  );
}

/**
 * The handle's vertical offset from the block's top: centered on the first
 * line, or on the top band of one handle height for blocks without text (an
 * image's middle could be 200px down).
 */
export function handleOffsetY(input: {
  blockTop: number;
  blockHeight: number;
  line: { top: number; height: number } | null;
  handleHeight: number;
}): number {
  const { blockTop, blockHeight, handleHeight } = input;
  // A first line outside the block's top band (text floated above it) counts as none.
  const line =
    input.line &&
    input.line.top >= blockTop - 1 &&
    input.line.top - blockTop <= handleHeight
      ? input.line
      : null;
  const band = Math.min(blockHeight, handleHeight);
  const center = line ? line.top + line.height / 2 : blockTop + band / 2;
  return Math.round(center - blockTop - handleHeight / 2);
}

/**
 * The first line with visible text (skipping text squeezed to 1px for screen
 * readers: a task item's checkbox label came first and put the handle between
 * items), else the first field, else null.
 */
function firstLineRect(dom: HTMLElement): DOMRect | null {
  const walker = document.createTreeWalker(dom, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (!n.nodeValue?.trim()) continue;
    const box = n.parentElement?.getBoundingClientRect();
    if (box && (box.width <= 1 || box.height <= 1)) continue;
    const range = document.createRange();
    range.selectNodeContents(n);
    const rect = range.getClientRects()[0];
    if (rect && rect.height > 0) return rect;
  }
  const field = dom.querySelector("input, select, textarea");
  if (field) {
    const rect = field.getBoundingClientRect();
    if (rect.height > 1 && rect.width > 1) return rect;
  }
  return null;
}

/**
 * Leaving the handle for anywhere but the body hides it. The plugin hides only
 * when the body is left; through the meta its current node is reset too (it
 * does not show again for the same node otherwise).
 */
function bindLeave(element: HTMLElement, editor: Editor): void {
  if (element.dataset.trameriaLeave) return;
  element.dataset.trameriaLeave = "1";
  element.addEventListener("mouseleave", (e) => {
    const to = e.relatedTarget;
    if (to instanceof Node && editor.view.dom.contains(to)) return;
    if (element.dataset.dragging === "true") return;
    // The open menu keeps it (the meta would drop the lock and the target).
    if (element.dataset.menuOpen === "true") return;
    editor.view.dispatch(editor.state.tr.setMeta("hideDragHandle", true));
  });
}

const typingBound = new WeakSet<HTMLElement>();

/**
 * Typing hides the handle. The plugin hides it on keydown; `beforeinput` also
 * covers paste from a context menu, dictation and drops, and never fires for
 * someone else's (remote) changes. Only while it is visible, to send no
 * transaction per keystroke.
 */
function bindHideWhileTyping(element: HTMLElement, editor: Editor): void {
  const dom = editor.view.dom;
  if (typingBound.has(dom)) return;
  typingBound.add(dom);
  dom.addEventListener("beforeinput", () => {
    if (element.dataset.menuOpen === "true") return;
    if (element.style.visibility === "hidden") return;
    editor.view.dispatch(editor.state.tr.setMeta("hideDragHandle", true));
  });
}

/** 0..1: a hint of what is carried; the drop line is what matters. */
const GHOST_OPACITY = "0.15";
const ghostBound = new WeakSet<HTMLElement>();

function dragTargetDom(
  element: HTMLElement,
  editor: Editor,
): HTMLElement | null {
  const pos = Number(element.dataset.dragPos ?? "-1");
  if (!Number.isInteger(pos) || pos < 0) return null;
  const dom = editor.view.nodeDOM(pos);
  return dom instanceof HTMLElement ? dom : null;
}

/**
 * The drag image: a faint copy of the block. Its box is its own stacking
 * context (`isolation`): Chromium draws a drag image from the nearest one and
 * otherwise baked the plugin's dark copy behind it. It lives under <body> (inside
 * the editor a clipping ancestor turned it blank); the theme is on <html>, so it
 * keeps the colors.
 */
function bindGhost(element: HTMLElement, editor: Editor): void {
  if (ghostBound.has(element)) return;
  ghostBound.add(element);
  element.addEventListener("dragstart", (e) => {
    if (!e.dataTransfer) return;
    // A move: left uninitialized, Chromium chose copy and showed a "+" cursor.
    e.dataTransfer.effectAllowed = "move";
    const source = dragTargetDom(element, editor);
    if (!source) return;
    const ghost = document.createElement("div");
    ghost.style.position = "absolute";
    ghost.style.top = "-10000px";
    ghost.style.left = "0";
    ghost.style.width = `${source.offsetWidth}px`;
    ghost.style.pointerEvents = "none";
    ghost.style.isolation = "isolate";
    const inner = document.createElement("div");
    inner.className = editor.view.dom.parentElement?.className ?? "";
    const clone = source.cloneNode(true) as HTMLElement;
    clone.style.margin = "0";
    clone.style.opacity = GHOST_OPACITY;
    inner.append(clone);
    ghost.append(inner);
    document.body.append(ghost);
    e.dataTransfer.setDragImage(ghost, 16, 8);
    const cleanup = (end: Event) => {
      ghost.remove();
      document.removeEventListener("dragend", cleanup);
      document.removeEventListener("drop", cleanup);
      resumeAfterDrag(editor, end);
    };
    document.addEventListener("dragend", cleanup);
    document.addEventListener("drop", cleanup);
  });
}

/**
 * Shows the handle again after a drop. The plugin hides it on dragend and shows
 * it only on a mousemove over the body; dropped in place, the mouse rests on
 * the handle and nothing came until it moved away and back. The current node is
 * reset first, or the same node would not be shown again.
 */
function resumeAfterDrag(editor: Editor, end: Event): void {
  if (!(end instanceof MouseEvent)) return;
  const { clientX, clientY } = end;
  if (clientX === 0 && clientY === 0) return;
  requestAnimationFrame(() => {
    if (editor.isDestroyed) return;
    editor.view.dispatch(editor.state.tr.setMeta("hideDragHandle", true));
    editor.view.dom.dispatchEvent(
      new MouseEvent("mousemove", { clientX, clientY, bubbles: true }),
    );
  });
}

/**
 * A click on the handle opens the block menu. A native drag fires no click, so
 * they never meet. No preventDefault on mousedown: Firefox would stop the drag.
 * While the menu is open the plugin is locked (and `data-menu-open` says so:
 * the lock itself cannot be read from outside).
 */
function bindMenu(element: HTMLElement, editor: Editor): void {
  if (element.dataset.trameriaMenu) return;
  element.dataset.trameriaMenu = "1";
  element.addEventListener("click", () => {
    if (element.dataset.dragging === "true") return;
    const blockId = element.dataset.blockId ?? null;
    const pos = Number(element.dataset.dragPos ?? "-1");
    if (blockId === null && !(Number.isInteger(pos) && pos >= 0)) return;
    if (element.dataset.menuOpen === "true") return;
    element.dataset.menuOpen = "true";
    editor.commands.lockDragHandle();
    const detail: BlockMenuEventDetail = {
      blockId,
      pos,
      anchor: element.getBoundingClientRect(),
      close: () => {
        element.dataset.menuOpen = "false";
        editor.commands.unlockDragHandle();
        editor.view.dispatch(editor.state.tr.setMeta("hideDragHandle", true));
      },
    };
    editor.view.dom.dispatchEvent(
      new CustomEvent(BLOCK_MENU_EVENT, { detail, bubbles: true }),
    );
  });
}
